<template>
  <div class="terminate-tenancy">
    <!-- Set tenancy end date Modal -->
    <hl-Modal v-if="showTenancyEndDateModal" class="text-center" :title="getTitle" :iconClass="getIcon">
      <!-- Basic & Premium -->
      <div v-if="newEndDateSet">
        <div class="mt-5">
          <hl-Button outline variant="secondary" @click="showTenancyEndDateModal = false">{{
            translationCloseWindow
          }}</hl-Button>
        </div>
      </div>
      <!-- / Basic & Premium -->

      <!-- Success -->
      <div v-else>
        <div class="col-12 col-md-6 mx-auto">
          <p>{{ translationSelectTenancyEndDate }}</p>
          <hl-Datepicker
            class="mt-5"
            :label="translationSetEndDate"
            v-model="newEndDate"
            @input="setNewEndDate"
          ></hl-Datepicker>
        </div>

        <div class="mt-5 pt-3">
          <hl-Button
            class="mx-2"
            variant="primary"
            @click="setNewTenancyEndDate"
            :disabled="!newEndDate || loading.endDate"
            :loading="loading.endDate"
            >{{ translationConfirm }}</hl-Button
          >
          <button class="btn btn-outline-secondary mx-2" @click="showTenancyEndDateModal = false">
            {{ translationCancel }}
          </button>
        </div>
      </div>
      <!-- / Success -->
    </hl-Modal>
    <!-- / Set tenancy end date Modal -->
    <!-- Blank tenancy end date || > 3 months from now -->
    <BlockContainer v-if="tenancyFreeOrActive" class="p-4 h-100">
      <h5>{{ translationTerminateTenancyTitle }}</h5>
      <p class="mt-4">{{ translationTerminateTenancyWhoWants }}</p>

      <div class="mt-5">
        <a :href="getLeaseTerminationLink">
          <hl-Button outline variant="secondary">{{ translationStartTermination }}</hl-Button>
        </a>
        <hl-Button class="ml-2" outline variant="secondary" @click="showTenancyEndDateModal = true">
          {{ translationSetEndDate }}
        </hl-Button>
      </div>
    </BlockContainer>
    <!-- / Blank tenancy end date || > 3 months from now -->

    <!-- Tenancy end date < 3 months -->
    <BlockContainer v-if="getTenancyStatus === 'in-cancellation'" class="p-5 h-100">
      <h5>{{ translationLeaseInClosingPhase }}</h5>
      <p class="mt-4">
        {{ translationTerminateSetEndDate }}
      </p>

      <div class="mt-5">
        <a :href="getLeaseTerminationLink">
          <button class="btn btn-warning mr-2">{{ translationStartTermination }}</button>
        </a>
        <button class="btn btn-warning ml-2" @click="showTenancyEndDateModal = true">
          {{ translationSetEndDate }}
        </button>
      </div>
    </BlockContainer>
    <!-- / Tenancy end date < 3 months -->

    <!-- Tenancy end date today or in the past -->
    <BlockContainer v-if="getTenancyStatus === 'cancelled'" class="p-5 h-100">
      <h5>{{ translationLeaseHasEnded }}</h5>
      <p class="mt-4">{{ translationReactivateTenancySetDateFuture }}</p>

      <div class="mt-5">
        <button class="btn btn-warning" @click="showTenancyEndDateModal = true">{{ translationSetEndDate }}</button>
      </div>
    </BlockContainer>
    <!-- / Tenancy end date today or in the past -->
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import Datepicker from '@modules/husleie-framework/src/components/forms/inputs/Datepicker.vue';
import BankAccountSelect from '@modules/husleie-framework/src/components/molecules/forms/bank-account/BankAccountSelect.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import i18nCommon from '@modules/i18n/mixins/Common';
import * as moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    'hl-Datepicker': Datepicker,
    'hl-BankAccountSelect': BankAccountSelect,
    BlockContainer,
  },
  data() {
    return {
      bankAccounts: [],
      currentBankAccount: null,
      loaded: {
        bankAccounts: false,
      },
      loading: {
        endDate: false,
      },
      newBankAccount: null,
      newEndDate: '',
      newEndDateSet: false,
      showTenancyEndDateModal: false,
    };
  },
  mixins: [i18nCommon],
  props: {
    tenancyId: [String, Number],
    landlordId: [String, Number],
  },
  computed: {
    ...mapGetters('tenancy', ['getTenancy', 'getTenancyStatus']),
    tenancyFreeOrActive() {
      return this.getTenancyStatus === 'free' || this.getTenancyStatus === 'active';
    },
    getIcon() {
      return this.newEndDateSet ? 'budicon-solid budicon-solid-tick-sign text-primary' : 'budicon-calendar';
    },
    getTitle() {
      return this.newEndDateSet ? this.translationEndDateSet : this.translationSetEndDate;
    },
    translationCloseWindow() {
      return this.translate('close window');
    },
    translationCancel() {
      return this.translate('cancel');
    },
    translationConfirm() {
      return this.translate('confirm');
    },
    translationErrorCouldNotSaveTenacy() {
      return this.translate('error.could not save tenancy');
    },
    translationLeaseHasEnded() {
      return this.translate('tenancy.terminate.translation lease has ended');
    },
    translationLeaseInClosingPhase() {
      return this.translate('tenancy.terminate.the lease is in the closing phase');
    },
    translationSelectTenancyEndDate() {
      return this.translate('tenancy.terminate.please select a tenancy end date');
    },
    translationSetEndDate() {
      return this.translate('tenancy.terminate.set end date');
    },
    translationEndDateSet() {
      return this.translate('tenancy.terminate.end date has been set {endDate}', {
        endDate: this.newEndDateFormatted,
      });
    },
    translationTerminateTenancyTitle() {
      return this.translate('tenancy.terminate.terminate the tenancy');
    },
    translationStartTermination() {
      return this.translate('tenancy.terminate.start termination');
    },
    translationTerminateTenancyWhoWants() {
      return this.translate('tenancy.terminate.does someone want to terminate the contract');
    },
    translationTerminateSetEndDate() {
      return this.translate('tenancy.terminate.the tenancy has an end date {endDate}', {
        endDate: this.formattedTenancyEndDate,
      });
    },
    translationReactivateTenancySetDateFuture() {
      return this.translate('tenancy.terminate.reactivate the tenancy by setting an end date in the future');
    },
    newEndDateFormatted() {
      return moment(this.newEndDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
    },
    formattedTenancyEndDate() {
      const date = this.getTenancy.endDate;
      if (date) {
        return moment(date).format('DD.MM.YYYY');
      }
      return 'N/A';
    },
    getLeaseTerminationLink() {
      if (this.tenancyId == null) return;
      return RedirectOperations.getURL(AppRedirect.TENANCY_TERMINATION, { tenancyId: this.tenancyId });
    },
  },
  methods: {
    ...mapActions('tenancy', [
      'updateTenancy',
      'fetchTenancy',
      'toggleEditTenancyBankAccountModal',
      'getBankInformationByAccountPrefix',
    ]),
    ...mapActions(['loadLandlordProfileBankAccounts']),
    async setNewTenancyEndDate() {
      // backend goes here
      const newEndDate = moment(this.newEndDate).format('YYYY-MM-DD');
      const tenancy = JSON.parse(JSON.stringify(this.getTenancy));
      tenancy.endDate = newEndDate;
      // delete readOnly keys from tenancy Object
      delete tenancy.id;
      delete tenancy.rentalObjectId;
      delete tenancy.hasDeposits;
      delete tenancy.rent;
      delete tenancy.tenants;
      delete tenancy.subProducts;
      delete tenancy.rentAdjustment;
      delete tenancy.subscription;

      this.loading.endDate = true;
      try {
        await this.updateTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: tenancy,
        });
        this.newEndDateSet = true;
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotSaveTenacy, 'error');
      }
      this.loading.endDate = false;
    },
    setNewEndDate(endDate) {
      this.newEndDate = endDate;
    },
  },
};
</script>
